// task-link — how a task's `link` may be rendered (SEM-5799 / M5).
//
// The inbox renders `link` as the product's first-class "go to source" action.
// The server now allowlists the value at write time
// (services/platform-graph/src/routes/task-link.ts), but the READER still has
// two jobs the server cannot do for it:
//   1. rows written BEFORE that allowlist existed may carry anything, including
//      `javascript:` — so an href that is not demonstrably safe is never put in
//      an <a> at all;
//   2. a first-party-but-off-app https link is a NAVIGATION AWAY, and must not
//      look identical to an in-product route. Trusted chrome around an unmarked
//      external destination is the whole phishing lever.
//
// Same byte-prefix discipline as the server module, and for the same reason:
// `new URL()` normalizes `\` to `/` in special schemes, so a parse-then-inspect
// check disagrees with the href the browser actually resolves. `/\evil.example`
// LOOKS root-relative and leaves the origin.
//
// This is deliberately a DIFFERENT predicate from the server's, not a copy of
// it: the server answers "may this be stored?" (needs the tenant's host
// allowlist), the client answers "how do I render what is already stored?"
// (has no allowlist, and treats every absolute URL as external — over-marking
// is the safe direction here).

export type TaskLinkKind =
  /** A root-relative in-product route — render as a plain in-app link. */
  | "internal"
  /** An absolute http(s) URL — render with an external affordance + host. */
  | "external"
  /** Anything else (javascript:, data:, //, /\, smuggled control bytes) — never an <a>. */
  | "unsafe";

const ROOT_RELATIVE_RE = /^\/(?![/\\])/;
const ABSOLUTE_HTTP_RE = /^https?:\/\/[^/\\?#]/i;

/** Browsers strip C0/space bytes before resolving, so an href containing them
 *  is not the href we inspected. See the server module's header. */
function hasControlOrSpace(link: string): boolean {
  for (let i = 0; i < link.length; i += 1) {
    const code = link.charCodeAt(i);
    if (code <= 0x20 || code === 0x7f) return true;
  }
  return false;
}

export function classifyTaskLink(link: string): TaskLinkKind {
  if (link === "" || hasControlOrSpace(link)) return "unsafe";
  if (ROOT_RELATIVE_RE.test(link)) return "internal";
  if (ABSOLUTE_HTTP_RE.test(link)) return "external";
  return "unsafe";
}

/**
 * The host to SHOW next to an external link, so the reader can see where the
 * trusted-looking button actually goes. Display only — never an authorization
 * input — so a plain slice between `://` and the first path/query/fragment
 * boundary is the honest answer (it shows userinfo tricks like
 * `app.novemai.com@evil.example` verbatim instead of hiding them).
 */
export function taskLinkHost(link: string): string {
  const schemeEnd = link.indexOf("://");
  if (schemeEnd === -1) return "";
  const rest = link.slice(schemeEnd + 3);
  const boundary = rest.search(/[/?#]/);
  return boundary === -1 ? rest : rest.slice(0, boundary);
}
